import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useSystemsContext, SystemSection } from "../../context/SystemsContext"; // 1. Import context and types

// Import the specific form components
import AssetProcessingForm from "./forms/AssetProcessing/AssetProcessingForm";
import AssetStorageForm from "./forms/AssetStorage/AssetStorageForm";
import AssetSyndicationForm from "./forms/AssetSyndication/AssetSyndicationForm";
import ClientProcessForm from "./forms/ClientProcessForm";

// Define the specific sections this component can handle
type ConfigSection = "clientProcess" | "assetProcessing" | "assetStorage" | "assetSyndication";

const sectionLabels: Record<ConfigSection, string> = {
  clientProcess: "Client Process",
  assetProcessing: "Asset Processing",
  assetStorage: "Asset Storage",
  assetSyndication: "Asset Syndication",
};

// 2. Remove all props, the component is now self-sufficient
const ConfigDetails: React.FC = () => {
  // 3. Get all data and the master handleChange function from the context
  const {
    formData: system,
    selectedConfig: expandedConfigId,
    selectedSection,
    handleChange,
  } = useSystemsContext();

  // This is a type guard. It ensures that if we are rendering this component,
  // selectedSection must be one of our valid ConfigSection types.
  if (!Object.keys(sectionLabels).includes(selectedSection)) {
    return null;
  }
  const section = selectedSection as ConfigSection;

  // This logic is perfectly preserved from the original component
  const selectedConfig = useMemo(() => {
    if (!system?.configurations || !expandedConfigId) return null;
    return system.configurations[expandedConfigId];
  }, [system, expandedConfigId]);

  if (!selectedConfig) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        No configuration selected.
      </Typography>
    );
  }

  const label = sectionLabels[section];
  const sectionData = selectedConfig?.[section] || {};
  const configName = selectedConfig?.clientProcess?.name || "Unnamed Config";

  // 4. This is the component's key responsibility: creating a specialized
  //    change handler that builds the correct path for the master handler.
  const handleFieldChange = (path: string, value: any) => {
    const fullPath = `configurations.${expandedConfigId}.${section}.${path}`;
    handleChange(fullPath, value);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {configName}
      </Typography>
      <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
        {label}
      </Typography>

      {/* 5. The child forms still receive props, but these props are now generated
          inside this component, not passed down from the top-level page. */}
      {section === "clientProcess" && (
        <ClientProcessForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetProcessing" && (
        <AssetProcessingForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetSyndication" && (
        <AssetSyndicationForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetStorage" && (
        <AssetStorageForm config={sectionData} handleChange={handleFieldChange} />
      )}
    </Box>
  );
};

export default ConfigDetails;